/* eslint-disable react/prop-types */
import React, { useCallback, useState } from "react";
import { Col, Row, Select, SelectProps } from "antd";
import {
  generateTestId,
  TestID,
  TestIDWrapper,
} from "../../shared/testids/testids";
import "./CORESelect.less";
import { CORERadiobuttonGroup } from "./CORERadioButton";
import { CORETag } from "../Content/CORETag";
import { COREIcon } from "../Content/COREIcon";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { WidthSize } from "./COREInput";
import { CORELoading } from "../Feedback/CORELoading";
import { CORETooltip } from "../Overlay/CORETooltip";
import { spacing } from "../../shared/global";
import classNames from "classnames";
import { DefaultOptionType } from "antd/lib/select";
import { COREDivider } from "../Layout/COREDivider";
import { useScrollablePopupContainer } from "./useScrollablePopupContainer";

type Size = "sm" | "md" | "lg";
export type CORESelectProps = Omit<
  SelectProps,
  "className" | "size" | "mode" | "maxTagPlaceholder"
> & {
  testID: TestID;
  size?: Size;
  widthSize?: WidthSize;
  mode?: "basic" | "multiple";
  headerMode?: boolean;
};

export type SuffixIconProp = {
  showSearch: boolean;
  open: boolean;
};

export const SuffixIcon: React.FC<SuffixIconProp> = ({ showSearch, open }) => {
  if (open && showSearch) {
    return (
      <COREIcon icon={icon({ name: "magnifying-glass", style: "regular" })} />
    );
  } else if (open) {
    return <COREIcon icon={icon({ name: "caret-up", style: "solid" })} />;
  } else {
    return <COREIcon icon={icon({ name: "caret-down", style: "solid" })} />;
  }
};

const removeSelectFocused = (visible: boolean) => {
  if (!visible) {
    const elements = document.getElementsByClassName("ant-select-focused");
    for (const element of elements) {
      element.classList.remove("ant-select-focused");
    }
  }
};

export const onPreventMouseDown = (event: {
  preventDefault: () => void;
  stopPropagation: () => void;
}) => {
  event.preventDefault();
  event.stopPropagation();
};
const CORESizeToAntSize: Record<Size, SelectProps["size"]> = {
  lg: "large",
  md: "middle",
  sm: "small",
};

const isFilled = (v: CORESelectProps["value"]) =>
  v != null &&
  (Array.isArray(v)
    ? v.length > 0
    : typeof v === "string"
    ? v.trim().length > 0
    : typeof v === "number"
    ? !Number.isNaN(v)
    : Boolean(v));

export const CORESelect: React.FC<CORESelectProps> = ({
  size = "md",
  testID,
  suffixIcon,
  menuItemSelectedIcon = (
    <COREIcon icon={icon({ name: "circle-check", style: "solid" })} />
  ),
  tagRender = (tagProps) => (
    <CORETag
      type={"basic"}
      label={tagProps.label}
      closable={tagProps.closable}
      onClose={tagProps.onClose}
      onMouseDown={onPreventMouseDown}
    />
  ),
  onDropdownVisibleChange,
  getPopupContainer,
  widthSize = "md",
  filterOption = (search, option) => {
    if (!option?.label) return false;
    return option.label.toString().toLowerCase().includes(search.toLowerCase());
  },
  mode,
  loading,
  value,
  onChange,
  headerMode,
  ...props
}) => {
  const [openedSelector, setOpenedSelector] = useState<boolean>(false);
  const scrollablePopupContainer = useScrollablePopupContainer();

  const valueSelect = value === null && mode === "multiple" ? [] : value;
  return (
    <CORELoading layout={"horizontal"} size={size} loading={loading} delay={0}>
      <TestIDWrapper testID={testID} className={"core-select"}>
        <div
          className={classNames(`input-width-${widthSize}`, {
            ["input-filled"]: isFilled(valueSelect),
          })}
        >
          <Select
            className={classNames({
              "header-mode": headerMode,
            })}
            maxTagPlaceholder={(tags) => {
              if (tags.length === 0) return;
              return (
                <CORETooltip
                  testID={generateTestId(
                    "admin",
                    "publication-modified-date-tooltip"
                  )}
                  overlay
                  width={"fit-content"}
                  trigger={"hover"}
                  message={
                    <Row
                      gutter={[spacing.xs, spacing.xs]}
                      justify={"center"}
                      className={"max-tag-placeholder-overflow"}
                    >
                      {tags.map((tag) => {
                        return (
                          <Col key={tag.key}>
                            {tagRender({
                              label: tag.label,
                              value: tag.value,
                              closable: true,
                              onClose: () => {
                                const newValue = valueSelect.filter(
                                  (v) => v !== tag.value
                                );
                                const newOptions = newValue.map((v) => ({
                                  label: v,
                                  value: v,
                                }));
                                onChange?.(newValue, newOptions);
                              },
                              disabled: false,
                            })}
                          </Col>
                        );
                      })}
                    </Row>
                  }
                >
                  <span onMouseDown={onPreventMouseDown}>
                    {`+${tags.length}`}
                  </span>
                </CORETooltip>
              );
            }}
            onChange={onChange}
            maxTagCount={"responsive"}
            dropdownStyle={{ padding: "2px" }}
            size={CORESizeToAntSize[size]}
            suffixIcon={
              suffixIcon ? (
                suffixIcon
              ) : (
                <SuffixIcon showSearch={true} open={openedSelector} />
              )
            }
            tagRender={tagRender}
            popupClassName={"core-select-dropdown"}
            getPopupContainer={
              getPopupContainer ?? scrollablePopupContainer.getPopupContainer
            }
            onDropdownVisibleChange={(visible) => {
              if (visible) scrollablePopupContainer.onOpen();
              removeSelectFocused(visible);
              onDropdownVisibleChange && onDropdownVisibleChange(visible);
              setOpenedSelector(visible);
            }}
            {...(mode === "multiple" && {
              mode,
              showArrow: true,
              allowClear: true,
              menuItemSelectedIcon: menuItemSelectedIcon,
            })}
            {...props}
            value={valueSelect}
            filterOption={filterOption}
            showSearch={true}
          />
        </div>
      </TestIDWrapper>
    </CORELoading>
  );
};

const flipValues = (
  options: CORESelectProps["options"],
  selectedValues: unknown
) => {
  if (!options) return [];

  const normalizedArray = Array.isArray(selectedValues)
    ? selectedValues
    : [selectedValues];

  // Flatten grouped options
  const flattenedOptions = options.flatMap((opt) => {
    if (typeof opt === "object" && "options" in opt && opt.options) {
      // This is a grouped option, return its children
      return opt.options;
    }
    return [opt];
  });

  const allValues = flattenedOptions.map((opt) =>
    typeof opt === "object" && "value" in opt ? opt.value : opt
  );
  return allValues.filter((val) => !normalizedArray.includes(val));
};

export type SelectorFilterMode = "include" | "exclude";
export type SelectorValueWithIncludeExclude<T> = Partial<{
  mode: SelectorFilterMode;
  displayValue: SelectProps<T>["value"];
  realValue: SelectProps<T>["value"];
}>;
export type CORESelectWithIncludeExcludeProps<T> = Omit<
  CORESelectProps,
  "value" | "onChange" | "mode"
> & {
  value?: SelectorValueWithIncludeExclude<T>;
  onChange?: (
    value: SelectorValueWithIncludeExclude<T> | null,
    option: DefaultOptionType | DefaultOptionType[]
  ) => void;
};
export const CORESelectWithIncludeExclude = <T,>({
  tagRender = (tagProps) => (
    <CORETag
      type={"basic"}
      label={tagProps.label}
      closable={tagProps.closable}
      onClose={tagProps.onClose}
      onMouseDown={onPreventMouseDown}
    />
  ),
  loading,
  value,
  onChange,
  options,
  dropdownRender,
  testID,
  ...props
}: CORESelectWithIncludeExcludeProps<T>) => {
  const [internalMode, setInternalMode] = useState<SelectorFilterMode>(
    value?.mode ?? "include"
  );

  const filterModeValue = value?.mode ?? internalMode;

  const onCustomOnchange = useCallback(
    (onChangeValue: T, option: DefaultOptionType | DefaultOptionType[]) => {
      if (!onChange) return;

      if (
        !onChangeValue ||
        (Array.isArray(onChangeValue) && onChangeValue.length === 0)
      ) {
        onChange(null, option);
        return;
      }

      const newValue: SelectorValueWithIncludeExclude<T> = {
        mode: filterModeValue,
        displayValue: onChangeValue,
        realValue:
          filterModeValue === "include"
            ? onChangeValue
            : (flipValues(options, onChangeValue) as T),
      };

      onChange(newValue, option);
    },
    [filterModeValue, onChange, options]
  );

  const handleFilterModeChange = useCallback(
    (newFilterMode: SelectorFilterMode) => {
      setInternalMode(newFilterMode);

      if (!onChange) return;

      const currentDisplayValue = value?.displayValue;

      if (
        !currentDisplayValue ||
        (Array.isArray(currentDisplayValue) && currentDisplayValue.length === 0)
      ) {
        return;
      }

      const newValue: SelectorValueWithIncludeExclude<T> = {
        mode: newFilterMode,
        displayValue: currentDisplayValue,
        realValue:
          newFilterMode === "include"
            ? currentDisplayValue
            : (flipValues(options, currentDisplayValue) as T),
      };

      onChange(newValue, []);
    },
    [value, onChange, options]
  );

  return (
    <CORESelect
      mode="multiple"
      loading={loading}
      value={value?.displayValue}
      onChange={onCustomOnchange}
      options={options}
      testID={testID}
      tagRender={(tagProps) => {
        const prefixTag = filterModeValue === "exclude" ? "-" : "";
        return tagRender({
          ...tagProps,
          label: (
            <>
              {prefixTag}
              {tagProps.label}
            </>
          ),
        });
      }}
      dropdownRender={(menu) => {
        const menuWithIncludeExclude = (
          <div className="selector-filter-mode">
            <CORERadiobuttonGroup
              testID={`${testID}/filter-mode`}
              direction="vertical"
              radioBtnValues={[
                {
                  name: "Include selection",
                  value: "include",
                  testID: `${testID}/filter-mode-include`,
                },
                {
                  name: "Exclude selection",
                  value: "exclude",
                  testID: `${testID}/filter-mode-exclude`,
                },
              ]}
              value={filterModeValue}
              onChange={(e) => {
                handleFilterModeChange(e.target.value);
              }}
            />
            <COREDivider space="xs" />
            {menu}
          </div>
        );

        return dropdownRender
          ? dropdownRender(menuWithIncludeExclude)
          : menuWithIncludeExclude;
      }}
      {...props}
    />
  );
};
